import produce from "immer";
import clamp from "lodash/clamp";
import find from "lodash/find";
import round from "lodash/round";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ReactComponent as IconOrientationReverse } from "../../../../icons/switch-orientation-reverse.svg";
import { ReactComponent as IconOrientation } from "../../../../icons/switch-orientation.svg";
import { ReactComponent as IconZoomIn } from "../../../../icons/zoom-in.svg";
import { ReactComponent as IconZoomOut } from "../../../../icons/zoom-out.svg";
import { Size, Unit } from "../../../../types/customization/box";
import { ContentEditablePicture, Cropping } from "../../../../types/customization/content";
import {
  Template,
  TemplateEditablePictureDefinition,
  TemplatePageDefinition,
} from "../../../../types/customization/template";
import { SelectionState } from "../../../../types/selection/selection";
import { hasBleedingEdge, svgBleedingMask } from "../../../../utils/customization/bleed";
import { isLowQualityPicture, sizeMmToPx } from "../../../../utils/customization/box";
import { drawClippedPath } from "../../../../utils/customization/clipping";
import { calcBounds } from "../../../../utils/customization/cropping";
import { findPhoto } from "../../../../utils/selection/finders";
import { imageUrlFromSelectionPhoto } from "../../../../utils/selection/photo";
import EditablePictureSvg from "../../../atoms/customization/EditablePictureSvg";
import LowQualityIndicator from "../../../atoms/customization/LowQualityIndicator";
import PanZoom from "../../../atoms/PanZoom";
import Slider from "../../../atoms/Slider";
import "./style.scss";

interface Props {
  element: ContentEditablePicture;
  elementDefinition: TemplateEditablePictureDefinition;
  elementSizeMm: Size<Unit.MM>;
  onChangeCropping: (cropping: Cropping) => void;
  pageDefinition: TemplatePageDefinition;
  ppmm: number;
  selection: SelectionState;
  template: Template;
}

const PictureEditor: FunctionComponent<Props> = ({
  element,
  elementDefinition,
  elementSizeMm,
  onChangeCropping,
  pageDefinition,
  ppmm,
  selection,
  template,
}) => {
  const intl = useIntl();
  const [grid, setGrid] = useState<boolean>(false);
  const [cropping, setCropping] = useState<Cropping>(element.cropping);

  useEffect(() => {
    setCropping(element.cropping);
  }, [element.cropping]);

  const elementSizePx = useMemo(() => sizeMmToPx(elementSizeMm, ppmm), [elementSizeMm, ppmm]);
  const mainStyle = useMemo(() => {
    return {
      height: elementSizePx.height.value + 2, // border
      width: elementSizePx.width.value + 2, // border
    };
  }, [elementSizePx]);

  const selectionPhoto = findPhoto(element.selectionPhotoKey, selection);
  const imageRatio = useMemo(
    () => selectionPhoto.width.value / selectionPhoto.height.value,
    [selectionPhoto]
  );

  const getCroppingRatio = useCallback(
    (rotation) => {
      const rotated = rotation % 180 === 90;
      return rotated
        ? elementSizeMm.height.value / elementSizeMm.width.value
        : elementSizeMm.width.value / elementSizeMm.height.value;
    },
    [elementSizeMm]
  );
  const croppingRatio = useMemo(
    () => getCroppingRatio(cropping.rotation),
    [cropping.rotation, getCroppingRatio]
  );

  const { constrainedByWidth, referenceLength, referenceLengthMax, referenceLengthMin } = useMemo(
    () => calcBounds({ cropping, croppingRatio, imageRatio }),
    [cropping, croppingRatio, imageRatio]
  );

  const rotate = useCallback(
    (rotation: number) => {
      const newCropping = produce(cropping, (draftCropping) => {
        draftCropping.default = false;

        const newRotation = (360 + draftCropping.rotation + rotation) % 360;
        switch (newRotation) {
          case 0:
          case 90:
          case 180:
          case 270:
            draftCropping.rotation = newRotation;
            break;
          default: {
            throw new Error(`Unsupported rotation ${newRotation}`);
          }
        }

        const newCroppingRatio = getCroppingRatio(newRotation);

        clampCropping(draftCropping, imageRatio, newCroppingRatio);
      });

      setCropping(newCropping);

      onChangeCropping(newCropping);
    },
    [cropping, getCroppingRatio, imageRatio, onChangeCropping, setCropping]
  );

  const onReferenceLengthChange = useCallback(
    (newReferenceLength: number) => {
      const newCropping = produce(cropping, (draftCropping) => {
        draftCropping.default = false;

        const relativeOffsetX = 0.5;
        const relativeOffsetY = 0.5;

        const currentReferenceLength = constrainedByWidth
          ? draftCropping.width.value
          : draftCropping.height.value;

        const scale = currentReferenceLength / newReferenceLength;

        const { scaleMax, scaleMin } = calcBounds({
          cropping: draftCropping,
          croppingRatio,
          imageRatio,
        });

        const limitedScale = clamp(scale, scaleMin, scaleMax);

        draftCropping.width.value = draftCropping.width.value / limitedScale;
        draftCropping.height.value = draftCropping.height.value / limitedScale;

        if (limitedScale !== 1) {
          draftCropping.x.value +=
            (draftCropping.width.value * limitedScale - draftCropping.width.value) *
            relativeOffsetX;
          draftCropping.y.value +=
            (draftCropping.height.value * limitedScale - draftCropping.height.value) *
            relativeOffsetY;
        }

        clampCropping(draftCropping, imageRatio, croppingRatio);
      });

      setCropping(newCropping);

      onChangeCropping(newCropping);
    },
    [constrainedByWidth, cropping, croppingRatio, imageRatio, onChangeCropping, setCropping]
  );

  const onZoom = useCallback(
    (scale: number, relativeOffsetX: number, relativeOffsetY: number) => {
      const newCropping = produce(cropping, (draftCropping) => {
        draftCropping.default = false;

        const { scaleMax, scaleMin } = calcBounds({
          cropping: draftCropping,
          croppingRatio,
          imageRatio,
        });

        const limitedScale = clamp(scale, scaleMin, scaleMax);

        draftCropping.width.value = draftCropping.width.value / limitedScale;
        draftCropping.height.value = draftCropping.height.value / limitedScale;

        if (limitedScale !== 1) {
          draftCropping.x.value +=
            (draftCropping.width.value * limitedScale - draftCropping.width.value) *
            relativeOffsetX;
          draftCropping.y.value +=
            (draftCropping.height.value * limitedScale - draftCropping.height.value) *
            relativeOffsetY;
        }

        clampCropping(draftCropping, imageRatio, croppingRatio);
      });

      setCropping(newCropping);

      onChangeCropping(newCropping);
    },
    [cropping, croppingRatio, imageRatio, onChangeCropping, setCropping]
  );

  const onPan = useCallback(
    (relativeDeltaX: number, relativeDeltaY: number) => {
      const newCropping = produce(cropping, (draftCropping) => {
        draftCropping.default = false;

        draftCropping.x.value += relativeDeltaX * draftCropping.width.value;
        draftCropping.y.value += relativeDeltaY * draftCropping.height.value;

        clampCropping(draftCropping, imageRatio, croppingRatio);
      });

      setCropping(newCropping);

      onChangeCropping(newCropping);
    },
    [cropping, croppingRatio, imageRatio, onChangeCropping, setCropping]
  );

  const hasBleedingEdges = useMemo(
    () => hasBleedingEdge(elementDefinition, pageDefinition),
    [elementDefinition, pageDefinition]
  );

  let shapeSvgContent: string | undefined;
  const elementShapeKey = elementDefinition.apparentShapeKey || elementDefinition.shapeKey;
  if (elementShapeKey) {
    const shapeDefinition = find(template.shapeDefinitions, ({ key }) => key === elementShapeKey);
    if (!shapeDefinition) {
      return <div className="PictureEditor">Error! ShapeDefinition not found.</div>;
    }

    shapeSvgContent = shapeDefinition.svgContent;
  } else if (pageDefinition.shapeKey) {
    const shapeDefinition = find(
      template.shapeDefinitions,
      ({ key }) => key === pageDefinition.shapeKey
    );
    if (!shapeDefinition) {
      return <div className="PictureEditor">Error! ShapeDefinition not found.</div>;
    }

    shapeSvgContent = drawClippedPath(
      elementDefinition,
      pageDefinition,
      shapeDefinition.svgContent,
      true
    );
  } else if (hasBleedingEdges.x || hasBleedingEdges.y) {
    shapeSvgContent = svgBleedingMask(elementDefinition, pageDefinition);
  }

  const imageUrlForEditor = imageUrlFromSelectionPhoto(selectionPhoto, "full");

  const zoomOut = () => onReferenceLengthChange(referenceLength * 1.2);
  const zoomIn = () => onReferenceLengthChange(referenceLength / 1.2);
  const rotateCcw = () => rotate(90);
  const rotateCw = () => rotate(-90);

  return (
    <div className="PictureEditor">
      <div className="PictureEditor-header">
        <div className="PictureEditor-header-item">
          <IconOrientation className="PictureEditor-icon" onClick={rotateCcw} />
          <IconOrientationReverse className="PictureEditor-icon" onClick={rotateCw} />
        </div>

        <div className="PictureEditor-header-itemPadding" />

        <div className="PictureEditor-header-item">
          <label>
            <input type="checkbox" onChange={() => setGrid((value) => !value)} checked={grid} />
            &nbsp; {intl.formatMessage({ id: "kustom_PictureEditor_grid" })}
          </label>
        </div>

        <div className="PictureEditor-header-itemPadding" />

        <div className="PictureEditor-header-item">
          <IconZoomOut className="PictureEditor-icon" onClick={zoomOut} />
          <Slider
            type="white"
            value={referenceLength}
            start={referenceLengthMax}
            end={referenceLengthMin}
            onChange={onReferenceLengthChange}
          />
          <IconZoomIn className="PictureEditor-icon" onClick={zoomIn} />
        </div>
      </div>

      <div className="PictureEditor-main" style={mainStyle}>
        <PanZoom onPan={onPan} onZoom={onZoom}>
          <EditablePictureSvg
            cropping={cropping}
            filterTag={element.filterTag}
            heightPx={elementSizePx.height.value}
            imageUrl={imageUrlForEditor}
            semiOpaque={true}
            shapeSvgContent={shapeSvgContent}
            widthPx={elementSizePx.width.value}
          />
        </PanZoom>

        {grid ? <div className="PictureEditor-grid" /> : null}

        {isLowQualityPicture(elementSizeMm, selectionPhoto, cropping, template.minDpi) && (
          <LowQualityIndicator
            type="floating"
            label={intl.formatMessage({
              id: "kustom_LowQualityIndicator_lowQuality",
            })}
          />
        )}
      </div>
    </div>
  );
};

export default PictureEditor;

const clampCropping = (naiveCropping: Cropping, imageRatio: number, croppingRatio: number) => {
  const { scaleMax, scaleMin, xMax, xMin, yMax, yMin } = calcBounds({
    cropping: naiveCropping,
    croppingRatio,
    imageRatio,
  });

  const limitedScale = clamp(1, scaleMin, scaleMax);

  if (limitedScale !== 1) {
    naiveCropping.width.value = naiveCropping.width.value / limitedScale;
    naiveCropping.height.value = naiveCropping.height.value / limitedScale;
    naiveCropping.x.value +=
      (naiveCropping.width.value * limitedScale - naiveCropping.width.value) / 2;
    naiveCropping.x.value +=
      (naiveCropping.height.value * limitedScale - naiveCropping.height.value) / 2;
  }

  naiveCropping.width.value = round(naiveCropping.width.value);
  naiveCropping.height.value = round(naiveCropping.height.value);
  naiveCropping.x.value = round(clamp(naiveCropping.x.value, xMin, xMax));
  naiveCropping.y.value = round(clamp(naiveCropping.y.value, yMin, yMax));
};
